import { PolicyInsight } from '@/components/policy-insight/PolicyInsight';
import { useFundingSourceDelete } from '@/hooks/useFundingSourceDelete';
import { useUnderwritingSearch } from '@/features/shop-insurance/hooks/use-underwriting-search';
import { useFundingSourceEditGuard } from '@/features/shop-insurance/hooks/use-funding-source-edit-guards';
import {
  selectClient,
  selectClientCalculationSettings,
  selectClientIsLoading,
  selectPolicyApplications,
  selectSession,
} from '@/model/selectors';
import {
  FundingSourceCostSlideOutContent,
  FundingSourceLtcPayoutSlideOutContent,
  FundingSourceTotalPayoutSlideOutContent,
  InsuranceWhiteBoxSlideOutContent,
  ROIGuidance,
  ROISlideOutContent,
  ReturnOnInvestmentCalculationOptions,
  SlideOutSheet,
  dispatchPolicyUpdate,
  putFundingSourcesForClientByClientIdRequest,
} from '@client';
import {
  putPolicyApplicationForClientByClientIdRequest,
  resetPolicyApplicationStateStatus,
  selectLastCreatedApplicationId,
  selectPolicyApplicationsStatus,
} from '@client/model';
import { getAiPromptType } from '@client/util';
import { Button, Popover, PopoverContent, PopoverTrigger, Switch } from '@design-system';
import { Tooltip } from '@mui/material';
import {
  FundingPolicyType,
  SessionType,
  formatLtcCostDisplay,
  formatROI,
  fundingPolicyTypeDefs,
  isJointPolicy,
  isNullOrUndefined,
  newUuid,
  formatCurrency as sharedFormatCurrency,
  zipCodeToState,
} from '@shared';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';
import { getProductAvailableStates } from '@shared/carrierStateAvailability';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { isPolicyEligibleForApplication } from '@shared/policy-application/policyApplicationDefs';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import produce from 'immer';
import { Columns3, Loader, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CgAsterisk } from 'react-icons/cg';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { cn } from '../lib/utils';
import { DataTable } from './DataTable';
import { SortHeader, renderHeaderCell, renderTableCell } from './DataTableColumn';
import {
  DebugIrrAction,
  DeleteAction,
  EditAction,
  FundingSourceActionMenu,
} from './FundingSourceActionMenu';
import { FundingSourceDeleteDialogs } from './FundingSourceDeleteDialogs';
import { IrrCashflowDebugModal } from './IrrCashflowDebugModal';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import { PoliciesComparisonModal as PoliciesComparisonModalLegacy } from './PoliciesComparisonModal';
import { StateOfResidenceSelector } from './StateOfResidenceSelector';
import { UnderwritingSlideOut } from './UnderwritingSlideOut';
import { UnderwritingStatusBadge } from './UnderwritingStatusBadge';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS_BY_POLICY_TYPE } from '@client/lib/nyc-lif-demo';

export type FundingTableRow = {
  id: string;
  type: 'selfFunding' | 'annuity' | 'insurance' | 'totals';
  show: boolean | null | 0;
  fundingSourceLabel: string;
  totalCost: number;
  ltcPayOut: number;
  partnerLtcPayOut?: number;
  nonLtcPayOut: number;
  totalPayOut: number;
  projectedReturnOnInvestment: number;
  projectedCompoundAnnualGrowthRate: number;
  projectedInternalRateOfReturn: number | undefined;
  investmentPeriodYears?: number;
  ltcCostCoveragePercent: number;
  openModal: (props: any) => void;
  deleteFundingSourceCallBack: () => void;
  fundingSourceId?: string;
  isDeactivated?: boolean;
  addedByAdvisorFullName?: string;
  addedViaShopInsurance?: boolean;
  isJointPolicy?: boolean;
  policyAddedAt?: number;
  underwritingStatus?: 'eligible' | 'ineligible' | 'unsure' | null;
  productName?: string;
  quoteId?: string;
  policyType?: FundingPolicyType | null;
  isEligibleForApplication?: boolean;
};

const formatCurrency = (value: number) => sharedFormatCurrency(value, 2);

const columnHelper = createColumnHelper<FundingTableRow>();

type FundingSourceTableProps = {
  setOpenModal: {
    [fundingSourceLabel: keyof FundingSources]: (...params: any[]) => void;
  };
  handleShowDownloadReport: () => void;
  className?: string;
  primaryButton?: React.ReactNode;
};

type CellStyleProps = {
  isTotal: boolean;
  color?: string;
  className?: string;
};

const getCellClassName = ({ isTotal, color, className }: CellStyleProps) => {
  return cn(className, color, isTotal && 'font-bold');
};

const renderCurrencyCell = (value: number, props: CellStyleProps) => {
  return <span className={getCellClassName(props)}>{formatCurrency(value)}</span>;
};

const renderLtcCostCell = (value: number, props: CellStyleProps) => {
  return (
    <span className={getCellClassName(props)}>
      <LtcCostDisplay value={value} />
    </span>
  );
};

const renderROICell = (value: string, props: CellStyleProps) => {
  return <span className={getCellClassName(props)}>{value}</span>;
};

function RecentlyAddedBadge() {
  return (
    <span className="bg-primary-50 text-primary ring-primary-200 inline-flex w-max items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset">
      Recently Added
    </span>
  );
}

export function FundingTable({
  setOpenModal,
  className,
  primaryButton,
  handleShowDownloadReport,
}: FundingSourceTableProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient)!;
  const clientIsLoading = useSelector(selectClientIsLoading);
  const policyApplications = useSelector(selectPolicyApplications) || [];
  const session = useSelector(selectSession);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const isWizardMode = session.sessionType === SessionType.wizard;

  // Underwriting slideout state
  const [showUnderwritingSlideOut, setShowUnderwritingSlideOut] = useState(false);
  const [selectedUnderwritingData, setSelectedUnderwritingData] = useState<{
    data: OpenAiCompletionDbRecord | null;
    productName: string;
  } | null>(null);

  // Start Application state
  const [showStateSelector, setShowStateSelector] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string>('');
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);
  const lastCreatedApplicationId = useSelector(selectLastCreatedApplicationId);
  const isCreatingPolicyApplication = policyApplicationStatus === 'creatingPolicyApplication';

  const clientZipCode = client?.intakeSurvey?.clientZipCode;
  const stateFromZipCode = clientZipCode ? zipCodeToState(clientZipCode)?.stateAbbreviation : '';

  // Initialize selected state when state selector opens - auto-select from zipcode
  useEffect(() => {
    if (showStateSelector && stateFromZipCode) {
      setSelectedState(stateFromZipCode);
    }
  }, [showStateSelector, stateFromZipCode]);

  // Handle navigation after application creation
  useEffect(() => {
    if (
      policyApplicationStatus === 'creatingPolicyApplicationComplete' &&
      lastCreatedApplicationId
    ) {
      dispatch(resetPolicyApplicationStateStatus());
      navigate(`/clients/${client.clientId}/policy-application/${lastCreatedApplicationId}`);
    }
  }, [policyApplicationStatus, lastCreatedApplicationId, client.clientId, dispatch, navigate]);

  const handleStartApplication = (row: FundingTableRow) => {
    if (!row.isEligibleForApplication || !row.policyType || !row.fundingSourceId) return;

    // Check if there's already an application for this funding source
    const existingApplication = policyApplications.find(
      app => app.fundingSourceId === row.fundingSourceId,
    );

    // If an application exists, use its state and navigate directly
    if (existingApplication && existingApplication.stateOfResidenceAbbreviation) {
      navigate(
        `/clients/${client.clientId}/policy-application/${existingApplication.clientPolicyApplicationId}`,
      );
      return;
    }

    // If state selector not shown yet, show it
    if (showStateSelector !== row.id) {
      setShowStateSelector(row.id);
      return;
    }

    // If state is selected, create the application
    if (selectedState && row.policyType) {
      const clientPolicyApplicationId = newUuid();
      dispatch(
        putPolicyApplicationForClientByClientIdRequest({
          clientPolicyApplicationId,
          clientId: client.clientId,
          advisorId: client.advisorId,
          partnerClientId: client.partnerClientId,
          fundingPolicyType: row.policyType,
          fundingSourceId: row.fundingSourceId,
          stateOfResidenceAbbreviation: selectedState,
          status: 'not_started',
        }),
      );
      // Reset state after dispatching
      setShowStateSelector(null);
      setSelectedState('');
    }
  };

  // Delete logic handled by custom hook
  const {
    showDeleteConfirmation,
    showDeleteBlockedDialog,
    setShowDeleteBlockedDialog,
    deleteBlockedReason,
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    setJointPolicyDeleteMode,
    deleteSelfFundingByFundingSourceIdCallBack,
    deleteAnnuityCallBack,
    deletePolicyByFundingSourceIdCallBack,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
  } = useFundingSourceDelete(client, policyApplications);

  const {
    showEditBlockedDialog,
    setShowEditBlockedDialog,
    editBlockedReason,
    guardedEdit,
  } = useFundingSourceEditGuard(policyApplications);

  const {
    fundingSources: {
      annuity,
      combinedFundingTotalCost,
      combinedProjectedCostCoverage,
      combinedNonLtcPayout,
      combinedTotalValue,
      combinedProjectedReturnOnInvestment,
      combinedProjectedCompoundAnnualGrowthRate,
      combinedProjectedInternalRateOfReturn,
      combinedInvestmentPeriodYears,
    },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    fundingSources,
  } = client;

  const clientHasMultipleJointPolicies = useMemo(() => {
    return checkForMultipleJointPolicies(multipleFundingSources);
  }, [multipleFundingSources]);

  // Fetch underwriting results for all insurance policies
  const productsToSearch = useMemo(() => {
    const products: string[] = [];

    Object.values(multipleFundingSources.ltcPolicy).forEach(policy => {
      let productName: string | undefined;

      // sourceQuote can be an object or a JSON string
      if (policy.sourceQuote) {
        if (typeof policy.sourceQuote === 'string') {
          try {
            const parsed = JSON.parse(policy.sourceQuote);
            productName = parsed.productName;
          } catch (e) {
            // Failed to parse sourceQuote JSON
          }
        } else if (typeof policy.sourceQuote === 'object') {
          productName = policy.sourceQuote.productName;
        }
      }

      if (productName) {
        products.push(productName);
      }
    });

    return [...new Set(products)]; // Remove duplicates
  }, [multipleFundingSources.ltcPolicy]);

  const { results: underwritingSearchResults } = useUnderwritingSearch({
    clientId: client?.clientId,
    productsToSearch,
    isJoint: false, // We'll determine this per policy
    shouldStart: productsToSearch.length > 0,
  });

  // Handler to open underwriting details slideout
  const handleViewUnderwritingDetails = (productName: string) => {
    let underwritingData: OpenAiCompletionDbRecord | null = null;

    // Determine if policy is joint based on the policy in the table
    const policy = Object.values(multipleFundingSources.ltcPolicy).find(p => {
      const pName =
        typeof p.sourceQuote === 'string'
          ? JSON.parse(p.sourceQuote).productName
          : p.sourceQuote?.productName;
      return pName === productName;
    });

    const isJoint = policy?.policyType ? fundingPolicyTypeDefs[policy.policyType].isJoint : false;

    const aiPromptType = getAiPromptType('', productName, isJoint);
    if (aiPromptType) {
      const resultKey = `${productName}:${aiPromptType}`;
      const result = underwritingSearchResults.get(resultKey);
      if (result?.fullData) {
        underwritingData = result.fullData;
      }
    }

    setSelectedUnderwritingData({
      data: underwritingData,
      productName,
    });
    setShowUnderwritingSlideOut(true);
  };

  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const [returnOnInvestmentState, setReturnOnInvestmentState] =
    useState<ReturnOnInvestmentCalculationType>(
      clientCalculationSettings?.returnOnInvestmentCalculationType ?? 'total',
    );

  const hasLtcPolicies = useMemo(() => {
    return Object.keys(multipleFundingSources.ltcPolicy).length > 0;
  }, [multipleFundingSources.ltcPolicy]);

  const mostRecentPolicyTimestamp = useMemo(() => {
    const policies = Object.values(multipleFundingSources.ltcPolicy);

    if (policies.length === 0) return null;

    const timestamps = policies
      .map(policy => policy.policyAddedAt)
      .filter((timestamp): timestamp is number => timestamp !== undefined && timestamp !== null);

    if (timestamps.length === 0) return null;

    return Math.max(...timestamps);
  }, [multipleFundingSources.ltcPolicy]);

  const toggleFundingSourceCallback = (fundingSourceRow: FundingTableRow, newStatus: boolean) => {
    const { type, fundingSourceId } = fundingSourceRow;
    switch (type) {
      case 'insurance':
      case 'selfFunding':
        if (fundingSourceId)
          dispatchPolicyUpdate(
            dispatch,
            client.clientId,
            fundingSourceId,
            multipleFundingSources,
            'isDeactivated',
            newStatus,
          );
        break;
      case 'annuity':
        const updatedFundingSources = produce(fundingSources, draft => {
          draft[type].isDeactivated = newStatus;
        });
        dispatch(
          putFundingSourcesForClientByClientIdRequest({
            clientId: client.clientId,
            fundingSources: updatedFundingSources,
          }),
        );
        break;
    }
  };

  // Create table data array with totals
  const data: FundingTableRow[] = [
    ...createSelfFundingRows({
      selfFundingSources: multipleFundingSources.selfFunding,
      allPhaseInflatedProfessionalShareCost,
      setOpenModal,
      onDelete: deleteSelfFundingByFundingSourceIdCallBack,
    }),
    ...createInsuranceRows({
      policies: multipleFundingSources.ltcPolicy,
      clientHasStartedLtc,
      allPhaseInflatedProfessionalShareCost,
      setOpenModal,
      onDelete: deletePolicyByFundingSourceIdCallBack,
      mutableClientPartner: client.mutableClientPartner,
      underwritingSearchResults,
    }),
    ...createAnnuityRows({
      annuity,
      allPhaseInflatedProfessionalShareCost,
      setOpenModal,
      onDelete: () => deleteAnnuityCallBack(client.fundingSources),
    }),
    createTotalsRow({
      combinedFundingTotalCost: combinedFundingTotalCost ?? 0,
      combinedProjectedCostCoverage: combinedProjectedCostCoverage ?? 0,
      combinedNonLtcPayout: combinedNonLtcPayout ?? 0,
      combinedTotalValue: combinedTotalValue ?? 0,
      combinedProjectedReturnOnInvestment: combinedProjectedReturnOnInvestment ?? 0,
      combinedProjectedCompoundAnnualGrowthRate: combinedProjectedCompoundAnnualGrowthRate ?? 0,
      combinedProjectedInternalRateOfReturn: combinedProjectedInternalRateOfReturn,
      combinedInvestmentPeriodYears: combinedInvestmentPeriodYears ?? 0,
    }),
  ].filter(row => row.show);

  useEffect(() => {
    if (data.length > 0) {
      handleShowDownloadReport();
    }
  }, [data.length, handleShowDownloadReport]);

  // Column Defs
  const columnDefinitions = [
    columnHelper.accessor('fundingSourceLabel', {
      id: 'fundingSourceLabel',
      meta: { label: 'Funding Type' },
      size: 180,
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Funding Type
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        const row = info.row.original;
        const maskedRow = clientHasMultipleJointPolicies && row.isDeactivated;
        const showRecentlyAddedBadge =
          row.type === 'insurance' && row.policyAddedAt === mostRecentPolicyTimestamp;
        const showUnderwritingBadge = row.type === 'insurance' && row.underwritingStatus;
        const isEligible = row.isEligibleForApplication && row.type === 'insurance';

        const labelContent =
          row.type === 'totals' ? (
            <span className="font-bold">{value}</span>
          ) : row.type === 'insurance' && !maskedRow ? (
            <div className="flex flex-col gap-1">
              <InlineWhiteBoxSlideOutButton
                fundingSourceId={row.fundingSourceId!}
                isWizardMode={isWizardMode}
              >
                <span className="hover:underline">
                  {flags.nycLifeDemo ? PRODUCTS_BY_POLICY_TYPE[row.policyType || 2919] : value}
                </span>
              </InlineWhiteBoxSlideOutButton>
              <div className="flex flex-wrap gap-1">
                {showRecentlyAddedBadge && <RecentlyAddedBadge />}
                {showUnderwritingBadge && (
                  <UnderwritingStatusBadge
                    status={row.underwritingStatus!}
                    onViewDetails={() => handleViewUnderwritingDetails(row.productName!)}
                    size="sm"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {value}
              <div className="flex flex-wrap gap-1">
                {showRecentlyAddedBadge && <RecentlyAddedBadge />}
                {showUnderwritingBadge && (
                  <UnderwritingStatusBadge
                    status={row.underwritingStatus!}
                    onViewDetails={() => handleViewUnderwritingDetails(row.productName!)}
                    size="sm"
                  />
                )}
              </div>
            </div>
          );

        // Wrap with Popover if eligible for application
        if (isEligible && row.type === 'insurance' && !maskedRow) {
          // Check if there's already an application for this funding source
          const existingApplication = policyApplications.find(
            app => app.fundingSourceId === row.fundingSourceId,
          );
          const hasExistingApplication =
            !!existingApplication && !!existingApplication.stateOfResidenceAbbreviation;

          const showSelector = showStateSelector === row.id && !hasExistingApplication;
          const isOpen = openPopoverId === row.id || showSelector;

          return renderTableCell(
            <div className="flex flex-col gap-1">
              <InlineWhiteBoxSlideOutButton
                fundingSourceId={row.fundingSourceId!}
                isWizardMode={isWizardMode}
              >
                <span className="hover:underline">
                  {flags.nycLifeDemo ? PRODUCTS_BY_POLICY_TYPE[row.policyType || 2919] : value}
                </span>
              </InlineWhiteBoxSlideOutButton>
              <div className="flex flex-wrap gap-1">
                {showRecentlyAddedBadge && <RecentlyAddedBadge />}
                {showUnderwritingBadge && (
                  <UnderwritingStatusBadge
                    status={row.underwritingStatus!}
                    onViewDetails={() => handleViewUnderwritingDetails(row.productName!)}
                    size="sm"
                  />
                )}
              </div>
              {!flags.nycLifeDemo && (
                <div
                  className={cn(
                    'transition-opacity',
                    isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                  )}
                >
                  <Popover
                    open={isOpen}
                    onOpenChange={open => {
                      if (open) {
                        setOpenPopoverId(row.id);
                      } else {
                        setOpenPopoverId(null);
                        setShowStateSelector(null);
                        setSelectedState('');
                      }
                    }}
                  >
                    <PopoverTrigger>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 h-7 w-fit self-start border px-2 text-xs font-medium"
                      >
                        {hasExistingApplication ? 'Continue Application' : 'Start Application'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="relative w-80"
                      align="start"
                    >
                      <button
                        onClick={() => {
                          setOpenPopoverId(null);
                          setShowStateSelector(null);
                          setSelectedState('');
                        }}
                        className="absolute right-2 top-2 opacity-70 hover:opacity-100"
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                      </button>
                      <div className="px-2">
                        {hasExistingApplication ? (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-900">
                              Continue your application
                            </div>
                            <div className="text-xs text-gray-700">
                              State: {existingApplication.stateOfResidenceAbbreviation}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleStartApplication(row)}
                              className="bg-primary hover:bg-primary-hover w-full"
                            >
                              Continue Application
                            </Button>
                          </div>
                        ) : showSelector ? (
                          <div className="space-y-3">
                            <StateOfResidenceSelector
                              value={selectedState}
                              onChange={setSelectedState}
                              disabled={true}
                              availableStates={
                                row.policyType
                                  ? getProductAvailableStates(row.policyType)
                                  : undefined
                              }
                            />
                            <Button
                              size="sm"
                              onClick={() => handleStartApplication(row)}
                              disabled={!selectedState || isCreatingPolicyApplication}
                              className="w-full"
                            >
                              {isCreatingPolicyApplication ? (
                                <Loader className="h-4 w-4 animate-spin" />
                              ) : (
                                'Continue to Application'
                              )}
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-900">
                              Ready to apply for this policy?
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleStartApplication(row)}
                              className="bg-primary hover:bg-primary-hover w-full"
                            >
                              Start Application
                            </Button>
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>,
          );
        }

        return renderTableCell(labelContent);
      },
    }),
    columnHelper.accessor('totalCost', {
      id: 'totalCost',
      meta: { label: 'Cost' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
            showRoiTooltip
          >
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceCostSlideOutContent />}
            >
              <span className="hover:underline">Cost</span>
            </SlideOutSheet>
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        const costDisplay = renderCurrencyCell(info.getValue(), {
          isTotal: row.type === 'totals',
          color: 'text-red-700',
          className: 'flex items-center gap-1.5',
        });

        return renderTableCell(
          row.type === 'insurance' ? (
            <InlineWhiteBoxSlideOutButton
              fundingSourceId={row.fundingSourceId!}
              isWizardMode={isWizardMode}
            >
              {costDisplay}
            </InlineWhiteBoxSlideOutButton>
          ) : (
            costDisplay
          ),
        );
      },
    }),
    columnHelper.accessor('ltcPayOut', {
      id: 'ltcPayOut',
      meta: { label: 'LTC Payout' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
            showRoiTooltip
          >
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceLtcPayoutSlideOutContent />}
            >
              <span className="hover:underline">LTC Payout</span>
            </SlideOutSheet>
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        const payoutDisplay = (
          <div
            className={getCellClassName({
              isTotal: row.type === 'totals',
              color: 'text-green-600',
              className: 'flex flex-col gap-0.5',
            })}
          >
            <span>
              <LtcCostDisplay value={info.getValue()} />
            </span>
            {row.partnerLtcPayOut && <PartnerLtcPayoutTooltip amount={row.partnerLtcPayOut} />}
          </div>
        );

        return renderTableCell(
          row.type === 'insurance' ? (
            <InlineWhiteBoxSlideOutButton
              fundingSourceId={row.fundingSourceId!}
              isWizardMode={isWizardMode}
            >
              {payoutDisplay}
            </InlineWhiteBoxSlideOutButton>
          ) : (
            payoutDisplay
          ),
        );
      },
    }),
    columnHelper.accessor('nonLtcPayOut', {
      id: 'nonLtcPayOut',
      meta: { label: 'Non-LTC Payout' },
      header: ({ column }) =>
        renderHeaderCell(
          <div className="flex items-center gap-0">
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceLtcPayoutSlideOutContent />}
            >
              <span className="hover:underline">Non-LTC Payout</span>
            </SlideOutSheet>
            <ROIGuidance className="ml-1 w-max p-0.5 [&_svg]:!size-3 [&_svg]:p-0" />
          </div>,
        ),
      cell: info => {
        const row = info.row.original;
        return renderTableCell(
          renderCurrencyCell(info.getValue(), {
            isTotal: row.type === 'totals',
          }),
        );
      },
    }),
    columnHelper.accessor('totalPayOut', {
      id: 'totalPayOut',
      meta: { label: 'Total Payout' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
            showRoiTooltip
          >
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceTotalPayoutSlideOutContent />}
            >
              <span className="hover:underline">Total Payout</span>
            </SlideOutSheet>
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        return renderTableCell(
          renderCurrencyCell(info.getValue(), {
            isTotal: row.type === 'totals',
          }),
        );
      },
    }),
    columnHelper.accessor('projectedReturnOnInvestment', {
      id: 'roi',
      meta: {
        label: returnOnInvestmentState === 'total' ? 'ROI' : 'Annual ROI',
      },
      sortingFn: (rowA, rowB) => {
        switch (returnOnInvestmentState) {
          case 'compoundAnnualGrowthRate':
            return (
              rowA.original.projectedCompoundAnnualGrowthRate -
              rowB.original.projectedCompoundAnnualGrowthRate
            );
          case 'internalRateOfReturn':
            return (
              (rowA.original.projectedInternalRateOfReturn ?? 0) -
              (rowB.original.projectedInternalRateOfReturn ?? 0)
            );
          case 'total':
          default:
            return (
              rowA.original.projectedReturnOnInvestment - rowB.original.projectedReturnOnInvestment
            );
        }
      },
      header: ({ column }) =>
        renderHeaderCell(
          <div className="flex w-full items-center">
            <SortHeader
              column={column}
              variant="link"
              showRoiTooltip
            >
              <SlideOutSheet
                title="FAQ"
                slideOutContent={<ROISlideOutContent />}
                maxWidth="custom-roi"
              >
                <span className="hover:underline">
                  {returnOnInvestmentState === 'total' ? 'ROI' : 'Annual ROI'}
                </span>
              </SlideOutSheet>
            </SortHeader>
          </div>,
        ),
      cell: info => {
        const row = info.row.original;
        const formattedValue = formatROI(
          {
            projectedReturnOnInvestment: row.projectedReturnOnInvestment,
            projectedCompoundAnnualGrowthRate: row.projectedCompoundAnnualGrowthRate,
            projectedInternalRateOfReturn: row.projectedInternalRateOfReturn,
            investmentPeriodYears: row.investmentPeriodYears ?? 0,
          },
          returnOnInvestmentState,
        );

        return renderTableCell(
          renderROICell(formattedValue, {
            isTotal: row.type === 'totals',
            className: 'whitespace-nowrap',
          }),
        );
      },
    }),
    columnHelper.accessor('addedByAdvisorFullName', {
      id: 'addedByAdvisorFullName',
      meta: { label: 'Added By' },
      enableHiding: true,
      header: () => renderHeaderCell('Added By'),
      cell: info => renderTableCell(info.getValue() ?? ''),
    }),
    columnHelper.display({
      id: 'actions',
      enableHiding: false,
      meta: { label: 'Actions' },
      header: () => renderHeaderCell('Actions'),
      cell: ({ row }) => {
        const fundingSource = row.original;
        const isDeactivated = fundingSource.isDeactivated === true;
        if (fundingSource.id === 'totals') {
          if (isWizardMode) {
            return renderTableCell(
              <FundingSourceActionMenu
                actions={[
                  <DebugIrrAction
                    key="debug-irr"
                    onClick={() => setIsDebugModalOpen(true)}
                  />,
                ]}
              />,
            );
          }
          return renderTableCell(null);
        }

        return renderTableCell(
          <div className="flex items-center justify-between gap-0.5 text-sm md:text-base">
            <FundingSourceActionMenu
              actions={[
                <EditAction
                  key="edit"
                  onClick={() => guardedEdit(fundingSource.fundingSourceId, fundingSource.policyType, fundingSource.openModal)}
                />,
                <DeleteAction
                  key="delete"
                  onClick={() => handleDeleteClick(fundingSource.deleteFundingSourceCallBack)}
                />,
              ]}
            />
            <button
              onClick={() => {
                toggleFundingSourceCallback(fundingSource, !isDeactivated);
              }}
              className={cn(
                'rounded-md px-4 py-1 text-sm',
                isDeactivated
                  ? 'bg-red-50 text-red-400 hover:bg-red-100'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              )}
            >
              {isDeactivated ? 'Off' : 'On'}
            </button>
          </div>,
        );
      },
    }),
  ];

  return (
    <>
      <DataTable
        className={className}
        columns={columnDefinitions as ColumnDef<FundingTableRow>[]}
        getRowId={row => row.id}
        isLoading={clientIsLoading}
        data={data as FundingTableRow[]}
        tableId="fundingSourceTable"
        variant="outlined"
        isRowDeactivated={row => row.isDeactivated === true}
        isRowMasked={{
          fn: row =>
            clientHasMultipleJointPolicies &&
            row.isDeactivated === true &&
            row.isJointPolicy === true,
          tooltipContent: 'Only a single joint policy can be active',
        }}
        neverMaskedColumns={['fundingSourceLabel', 'actions']}
        initialColumnVisibility={{
          addedByAdvisorFullName: false,
        }}
        summaryRowId={'totals'}
        primaryButton={primaryButton}
        headerExtras={
          <>
            {hasLtcPolicies &&
              (flags.policiesComparisonV2 ? (
                <PoliciesComparisonModal>
                  <Button
                    variant="outline"
                    className="w-full md:w-max"
                  >
                    <Columns3 />
                    Compare Policies
                  </Button>
                </PoliciesComparisonModal>
              ) : (
                <PoliciesComparisonModalLegacy>
                  <Button
                    variant="outline"
                    className="w-full md:w-max"
                  >
                    <Columns3 />
                    Compare Policies
                  </Button>
                </PoliciesComparisonModalLegacy>
              ))}
            <ReturnOnInvestmentCalculationOptions
              discountRate={clientCalculationSettings?.discountRate}
              calculationType={returnOnInvestmentState}
              onChange={setReturnOnInvestmentState}
              isButtonStyle={true}
            />
          </>
        }
        useCustomScrollbar
      />
      <FundingSourceDeleteDialogs
        showDeleteConfirmation={showDeleteConfirmation}
        handleCancelDelete={handleCancelDelete}
        handleConfirmDelete={handleConfirmDelete}
        showDeleteBlockedDialog={showDeleteBlockedDialog}
        setShowDeleteBlockedDialog={setShowDeleteBlockedDialog}
        deleteBlockedReason={deleteBlockedReason}
        showEditBlockedDialog={showEditBlockedDialog}
        setShowEditBlockedDialog={setShowEditBlockedDialog}
        editBlockedReason={editBlockedReason}
        showJointPolicyDeleteDialog={showJointPolicyDeleteDialog}
        handleJointPolicyDeleteCancel={handleJointPolicyDeleteCancel}
        handleJointPolicyDeleteConfirm={handleJointPolicyDeleteConfirm}
        jointPolicyDeleteMode={jointPolicyDeleteMode}
        setJointPolicyDeleteMode={setJointPolicyDeleteMode}
        clientName={client.clientFirstName}
        partnerName={client.mutableClientPartner?.clientFirstName}
      />
      <IrrCashflowDebugModal
        open={isDebugModalOpen}
        onClose={() => setIsDebugModalOpen(false)}
        client={client}
      />
      {!flags.nycLifeDemo && (
        <UnderwritingSlideOut
          open={showUnderwritingSlideOut}
          onClose={() => setShowUnderwritingSlideOut(false)}
          underwritingData={selectedUnderwritingData?.data ?? null}
          productName={selectedUnderwritingData?.productName ?? ''}
        />
      )}
    </>
  );
}

function checkForMultipleJointPolicies(multipleFundingSources: MultipleFundingSources): boolean {
  return Object.values(multipleFundingSources.ltcPolicy).some(
    policy => policy.policyType && fundingPolicyTypeDefs[policy.policyType].isJoint,
  );
}

function getSelfFundingName(name?: string | null) {
  const defaultName = 'Self-funding';
  return !name || name === defaultName ? defaultName : `${defaultName} (${name})`;
}

function createSelfFundingRows({
  selfFundingSources,
  allPhaseInflatedProfessionalShareCost,
  setOpenModal,
  onDelete,
}: {
  selfFundingSources: { [fundingSourceId: string]: SelfFundingSource };
  allPhaseInflatedProfessionalShareCost: number;
  setOpenModal: FundingSourceTableProps['setOpenModal'];
  onDelete: (id: string) => void;
}): FundingTableRow[] {
  return Object.entries(selfFundingSources)
    .sort(([, a], [, b]) => (b.selfFundingTotalValue ?? 0) - (a.selfFundingTotalValue ?? 0))
    .map(([fundingSourceId, selfFunding]) => {
      const areSelfFundingFieldsPopulated =
        (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
        (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
        (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

      const hasSelfFunding = selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;

      return {
        id: fundingSourceId,
        type: 'selfFunding' as const,
        show: hasSelfFunding,
        fundingSourceId,
        fundingSourceLabel: getSelfFundingName(selfFunding?.selfFundingName),
        totalCost: selfFunding.selfFundingTotalCost ?? 0,
        ltcPayOut: selfFunding.selfFundingProjectedCostCoverage ?? 0,
        ltcCostCoveragePercent:
          (selfFunding.selfFundingProjectedCostCoverage ?? 0) /
          allPhaseInflatedProfessionalShareCost,
        nonLtcPayOut: selfFunding.selfFundingNonLtcPayout ?? 0,
        totalPayOut: selfFunding.selfFundingTotalValue ?? 0,
        projectedReturnOnInvestment: selfFunding.selfFundingProjectedReturnOnInvestment ?? 0,
        projectedCompoundAnnualGrowthRate:
          selfFunding.selfFundingProjectedCompoundAnnualGrowthRate ?? 0,
        projectedInternalRateOfReturn: selfFunding.selfFundingProjectedInternalRateOfReturn,
        investmentPeriodYears: selfFunding.investmentPeriodYears ?? 0,
        openModal: () => setOpenModal.selfFunding(fundingSourceId),
        deleteFundingSourceCallBack: () => onDelete(fundingSourceId),
        addedByAdvisorFullName: selfFunding.addedByAdvisorFullName,
        isDeactivated: selfFunding.isDeactivated,
      };
    })
    .filter(row => row.show);
}

function createInsuranceRows({
  policies,
  clientHasStartedLtc,
  allPhaseInflatedProfessionalShareCost,
  setOpenModal,
  onDelete,
  mutableClientPartner,
  underwritingSearchResults,
}: {
  policies: { [fundingSourceId: string]: PolicyFundingSource };
  clientHasStartedLtc: boolean;
  allPhaseInflatedProfessionalShareCost: number;
  setOpenModal: FundingSourceTableProps['setOpenModal'];
  onDelete: (id: string) => void;
  mutableClientPartner: Client | null;
  underwritingSearchResults: Map<
    string,
    {
      status: 'eligible' | 'ineligible' | 'unsure' | null;
      fullData: OpenAiCompletionDbRecord | null;
    }
  >;
}): FundingTableRow[] {
  return Object.entries(policies)
    .sort(([, a], [, b]) => (b.policyTotalValue ?? 0) - (a.policyTotalValue ?? 0))
    .map(([fundingSourceId, policy]) => {
      // Get product name from sourceQuote (can be object or JSON string)
      let productName: string | undefined;
      if (policy.sourceQuote) {
        if (typeof policy.sourceQuote === 'string') {
          try {
            const parsed = JSON.parse(policy.sourceQuote);
            productName = parsed.productName;
          } catch (e) {
            // sourceQuote is not valid JSON
          }
        } else if (typeof policy.sourceQuote === 'object') {
          productName = policy.sourceQuote.productName;
        }
      }

      // Get underwriting status for this policy
      const isJoint =
        !isNullOrUndefined(policy.policyType) && fundingPolicyTypeDefs[policy.policyType].isJoint;
      let underwritingStatus: 'eligible' | 'ineligible' | 'unsure' | null = null;

      if (productName) {
        const aiPromptType = getAiPromptType('', productName, isJoint);
        if (aiPromptType) {
          const resultKey = `${productName}:${aiPromptType}`;
          const result = underwritingSearchResults.get(resultKey);
          if (result) {
            underwritingStatus = result.status;
          }
        }
      }

      const isEligibleForApplication = isPolicyEligibleForApplication(policy);

      return {
        id: fundingSourceId,
        type: 'insurance',
        show: policy.hasPolicyFunding,
        fundingSourceId,
        fundingSourceLabel: policy.policyType
          ? fundingPolicyTypeDefs[policy.policyType].label
          : 'Insurance',
        totalCost: policy.policyTotalCost ?? 0,
        ltcPayOut: clientHasStartedLtc
          ? (policy.policyCoverageOfRemainingLtcCosts ?? 0)
          : (policy.projectedCostCoverage ?? 0),
        ltcCostCoveragePercent:
          (clientHasStartedLtc
            ? (policy.policyCoverageOfRemainingLtcCosts ?? 0)
            : (policy.projectedCostCoverage ?? 0)) / allPhaseInflatedProfessionalShareCost,
        nonLtcPayOut: policy.policyNonLtcPayout ?? 0,
        totalPayOut: policy.policyTotalValue ?? 0,
        partnerLtcPayOut: isJointPolicy(policy.policyType, mutableClientPartner)
          ? (mutableClientPartner?.fundingSources?.ltcPolicy?.projectedCostCoverage ??
            mutableClientPartner?.fundingSources?.ltcPolicy?.policyCoverageOfRemainingLtcCosts ??
            0)
          : undefined,
        projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment ?? 0,
        projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate ?? 0,
        investmentPeriodYears: policy.investmentPeriodYears ?? 0,
        projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
        openModal: () => setOpenModal.ltcPolicy(policy.policyType, fundingSourceId),
        deleteFundingSourceCallBack: () => onDelete(fundingSourceId),
        isDeactivated: policy.isDeactivated,
        addedByAdvisorFullName: policy.addedByAdvisorFullName,
        addedViaShopInsurance: policy.addedViaShopInsurance,
        isJointPolicy: isJoint,
        policyAddedAt: policy.policyAddedAt,
        underwritingStatus,
        productName,
        quoteId: policy.insuranceQuoteId,
        policyType: policy.policyType,
        isEligibleForApplication: isEligibleForApplication ?? undefined,
      };
    });
}

function createAnnuityRows({
  annuity,
  allPhaseInflatedProfessionalShareCost,
  setOpenModal,
  onDelete,
}: {
  annuity: AnnuitySource;
  allPhaseInflatedProfessionalShareCost: number;
  setOpenModal: FundingSourceTableProps['setOpenModal'];
  onDelete: () => void;
}): FundingTableRow[] {
  if (!annuity.hasAnnuity) return [];

  return [
    {
      id: 'annuity',
      type: 'annuity',
      show: true,
      fundingSourceLabel: 'Annuity',
      totalCost: annuity.annuityTotalCost ?? 0,
      ltcPayOut: annuity.annuityProjectedCostCoverage ?? 0,
      ltcCostCoveragePercent:
        (annuity.annuityProjectedCostCoverage ?? 0) / allPhaseInflatedProfessionalShareCost,
      nonLtcPayOut: annuity.annuityNonLtcPayout ?? 0,
      totalPayOut: annuity.annuityTotalValue ?? 0,
      projectedReturnOnInvestment: annuity.annuityProjectedReturnOnInvestment ?? 0,
      projectedCompoundAnnualGrowthRate: annuity.annuityProjectedCompoundAnnualGrowthRate ?? 0,
      investmentPeriodYears: annuity.investmentPeriodYears ?? 0,
      projectedInternalRateOfReturn: annuity.annuityProjectedInternalRateOfReturn,
      openModal: setOpenModal.annuity,
      deleteFundingSourceCallBack: onDelete,
      addedByAdvisorFullName: annuity.addedByAdvisorFullName,
      isDeactivated: annuity.isDeactivated,
    },
  ];
}

function createTotalsRow({
  combinedFundingTotalCost,
  combinedProjectedCostCoverage,
  combinedNonLtcPayout,
  combinedTotalValue,
  combinedProjectedReturnOnInvestment,
  combinedProjectedCompoundAnnualGrowthRate,
  combinedProjectedInternalRateOfReturn,
  combinedInvestmentPeriodYears,
}: {
  combinedFundingTotalCost: number;
  combinedProjectedCostCoverage: number;
  combinedNonLtcPayout: number;
  combinedTotalValue: number;
  combinedProjectedReturnOnInvestment: number;
  combinedProjectedCompoundAnnualGrowthRate: number;
  combinedProjectedInternalRateOfReturn: number | undefined;
  combinedInvestmentPeriodYears: number;
}) {
  return {
    id: 'totals',
    type: 'totals' as const,
    show: true,
    fundingSourceLabel: 'Total',
    totalCost: combinedFundingTotalCost,
    ltcPayOut: combinedProjectedCostCoverage,
    nonLtcPayOut: combinedNonLtcPayout,
    totalPayOut: combinedTotalValue,
    projectedReturnOnInvestment: combinedProjectedReturnOnInvestment,
    projectedCompoundAnnualGrowthRate: combinedProjectedCompoundAnnualGrowthRate,
    projectedInternalRateOfReturn: combinedProjectedInternalRateOfReturn,
    ltcCostCoveragePercent: 0, // Not needed for totals
    openModal: () => {}, // Not needed for totals
    deleteFundingSourceCallBack: () => {}, // Not needed for totals
    investmentPeriodYears: combinedInvestmentPeriodYears,
  };
}

type InlineWhiteBoxSlideOutButtonProps = {
  fundingSourceId: string;
  labelColor?: string;
  labelHoverColor?: string;
  children?: React.ReactNode;
  isWizardMode?: boolean;
};
function InlineWhiteBoxSlideOutButton({
  labelColor,
  fundingSourceId,
  children,
  isWizardMode,
}: InlineWhiteBoxSlideOutButtonProps) {
  const [showNewInsight, setShowNewInsight] = useState(false);

  const slideOutContent = (
    <div className="flex flex-col gap-4">
      {isWizardMode && (
        <div className="mt-8 flex items-center gap-3 bg-white pb-2">
          <Switch
            checked={showNewInsight}
            onCheckedChange={setShowNewInsight}
          />
          <span
            className={cn(
              'text-sm',
              showNewInsight ? 'font-medium text-gray-900' : 'text-gray-700',
            )}
          >
            Try New Policy Insight (Beta)
          </span>
        </div>
      )}
      <div className={showNewInsight && isWizardMode ? '' : 'mt-6'}>
        {showNewInsight && isWizardMode ? (
          <PolicyInsight fundingSourceId={fundingSourceId} />
        ) : (
          <InsuranceWhiteBoxSlideOutContent fundingSourceId={fundingSourceId} />
        )}
      </div>
    </div>
  );

  return (
    <SlideOutSheet
      title="Policy Insight"
      slideOutContent={slideOutContent}
      maxWidth="max-w-md"
      noMargin
    >
      <p className={cn(`text-left hover:underline`, labelColor ? labelColor : '')}>{children}</p>
    </SlideOutSheet>
  );
}

function PartnerLtcPayoutTooltip({ amount }: { amount: number }) {
  const roundingStep = useLtcCostDisplayPrecision();
  const { formattedDisplay } = formatLtcCostDisplay(amount, { roundingStep });
  return (
    <Tooltip
      arrow={true}
      placement="right"
      title={<p className="text-sm">Payout for partner</p>}
    >
      <span className="flex items-center gap-0.5 text-xs text-gray-700">
        {formattedDisplay}
        <CgAsterisk />
      </span>
    </Tooltip>
  );
}
